import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import posthog from 'posthog-js'
import { handleMarketingAttribution, cleanMarketingParamsFromUrl, hasMarketingAttribution } from '@/lib/posthog-attribution'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Track if we've already processed marketing attribution this session
  const attributionProcessedRef = useRef(false)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Handle marketing attribution before identifying the user
      // This ensures the marketing site visitor gets properly merged with the app user
      if (!attributionProcessedRef.current && hasMarketingAttribution()) {
        handleMarketingAttribution()
        cleanMarketingParamsFromUrl()
        attributionProcessedRef.current = true
      }

      // Identify user in PostHog if session exists
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name,
        })
        // Reload feature flags after identify so they evaluate with the new person properties
        posthog.reloadFeatureFlags()
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Handle marketing attribution before identifying the user on sign in
      // This is important for new signups coming from the marketing site
      if (event === 'SIGNED_IN' && !attributionProcessedRef.current && hasMarketingAttribution()) {
        handleMarketingAttribution()
        cleanMarketingParamsFromUrl()
        attributionProcessedRef.current = true
      }

      // Identify user in PostHog on sign in
      if (event === 'SIGNED_IN' && session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name,
        })
        // Reload feature flags after identify so they evaluate with the new person properties
        posthog.reloadFeatureFlags()
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        },
      })
      if (error) throw error
    } catch (error) {
      console.error('Error signing in with Google:', error)
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      // Check if we're ACTUALLY running in extension mode
      // Must have BOTH: fromExtension flag AND be in an iframe
      const urlParams = new URLSearchParams(window.location.search)
      const fromExtensionParam = urlParams.get('fromExtension') === '1'
      const fromExtensionStorage = sessionStorage.getItem('calintent_in_extension') === 'true'
      const isInIframe = window.parent !== window
      const fromExtension = (fromExtensionParam || fromExtensionStorage) && isInIframe

      console.log('[Auth Context] 🚪 Logout initiated')
      console.log('[Auth Context] In iframe?', isInIframe)
      console.log('[Auth Context] In extension?', fromExtension)

      // Use 'local' scope to clear local session
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      
      // Ignore AuthSessionMissingError - it just means we're already logged out
      if (error && error.name !== 'AuthSessionMissingError') {
        throw error
      }

      // Reset PostHog identity on logout
      posthog.reset()

      console.log('[Auth Context] ✅ Supabase signOut successful')
      
      // Always clear the sessionStorage flag
      sessionStorage.removeItem('calintent_in_extension')

      // If in extension mode, broadcast logout event to extension
      if (fromExtension) {
        console.log('[Auth Context] 🚪 Broadcasting CALINTENT_LOGOUT to extension...')
        window.parent.postMessage(
          { type: 'CALINTENT_LOGOUT' },
          '*' // Will be validated by receivers
        )
        console.log('[Auth Context] ✅ Logout message sent to extension')
      } else {
        console.log('[Auth Context] ✅ Webapp logout complete')
      }
    } catch (error) {
      console.error('[Auth Context] ❌ Error signing out:', error)
      // Don't throw the error - we still want to clear local state
      // even if the server-side sign out fails
    }
  }, [])

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signInWithGoogle,
    signOut,
  }), [user, session, loading, signInWithGoogle, signOut])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
